// =============================================================================
// FILE: packages/renderer/src/components/fris/FrisIndicator.tsx
// PURPOSE: Per-module "Feature Readiness" lightbulb icon in the TopBar.
//   Green  = Production Ready
//   Yellow = Beta / Testing
//   Red    = Under Development
//   Click  → floating popover with "What's Working" + "What's Coming Next"
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { Lightbulb, CheckCircle2, ArrowRight, AlertCircle, Info } from 'lucide-react';
import { getFrisContent, type FrisLevel } from '../../config/fris.content.js';
import { useFrisStatus } from '../../hooks/useFris.js';

// ── Level styling ─────────────────────────────────────────────────────────────

const LEVEL_STYLE: Record<FrisLevel, {
  glow:    string;
  fill:    string;
  badge:   string;
  label:   string;
  tagline: string;
}> = {
  green: {
    glow:    '0 0 10px rgba(52,211,153,0.75), 0 0 22px rgba(52,211,153,0.35)',
    fill:    '#34D399',
    badge:   'rgba(52,211,153,0.14)',
    label:   'Production Ready',
    tagline: 'This module is stable and fully operational.',
  },
  yellow: {
    glow:    '0 0 10px rgba(251,191,36,0.70), 0 0 22px rgba(251,191,36,0.30)',
    fill:    '#FBBF24',
    badge:   'rgba(251,191,36,0.12)',
    label:   'Beta / Testing',
    tagline: 'Core features work. Some flows are still maturing.',
  },
  red: {
    glow:    '0 0 10px rgba(248,113,113,0.65), 0 0 20px rgba(248,113,113,0.28)',
    fill:    '#F87171',
    badge:   'rgba(248,113,113,0.12)',
    label:   'Under Development',
    tagline: 'Work in progress — expect rough edges.',
  },
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  /** Matches a key in FRIS_CONTENT and the FRIS probe id. */
  featureId: string;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FrisIndicator({ featureId, className = '' }: Props): JSX.Element {
  const [open, setOpen]     = useState(false);
  const rootRef             = useRef<HTMLDivElement>(null);
  const { data: frisData }  = useFrisStatus();

  // Merge static content with live FRIS probe level
  const staticContent = getFrisContent(featureId);
  const liveProbe     = frisData?.find((f) => f.id === featureId);
  const level: FrisLevel = liveProbe?.status ?? staticContent.level;
  const summary           = liveProbe?.reason ?? staticContent.summary;

  const style = LEVEL_STYLE[level];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`relative inline-flex items-center ${className}`}
      data-testid={`fris-indicator-${featureId}`}
    >
      {/* ── Lightbulb trigger ─────────────────────────────────────────────── */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={`Feature readiness: ${staticContent.label} — ${style.label}`}
        aria-expanded={open}
        className="flex items-center justify-center rounded-lg transition-all duration-200"
        style={{
          width:  28,
          height: 28,
          background: open ? style.badge : 'transparent',
          border: `1px solid ${open ? style.fill + '44' : 'transparent'}`,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = style.badge;
          (e.currentTarget as HTMLButtonElement).style.borderColor = style.fill + '40';
        }}
        onMouseLeave={(e) => {
          if (!open) {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent';
          }
        }}
      >
        <Lightbulb
          className="h-3.5 w-3.5 transition-all duration-300"
          strokeWidth={1.75}
          style={{
            color:     style.fill,
            filter:    open ? style.glow : `0 0 6px ${style.fill}55`,
            transform: open ? 'scale(1.1)' : 'scale(1)',
          }}
        />
      </button>

      {/* ── Popover ──────────────────────────────────────────────────────────── */}
      {open && (
        <div
          data-testid={`fris-popover-${featureId}`}
          role="dialog"
          aria-label={`${staticContent.label} readiness details`}
          style={{
            position:   'absolute',
            top:        'calc(100% + 8px)',
            left:       '50%',
            transform:  'translateX(-50%)',
            zIndex:     9999,
            width:      320,
            background: 'rgba(6, 11, 22, 0.97)',
            border:     `1px solid ${style.fill}28`,
            borderRadius: 12,
            boxShadow:  `0 8px 40px rgba(0,0,0,0.65), 0 0 0 1px ${style.fill}10, 0 0 24px ${style.fill}10`,
            backdropFilter: 'blur(18px)',
            overflow:   'hidden',
            animation:  'frisPopIn 0.18s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <style>{`
            @keyframes frisPopIn {
              from { opacity: 0; transform: translateX(-50%) scale(0.92) translateY(-6px); }
              to   { opacity: 1; transform: translateX(-50%) scale(1) translateY(0); }
            }
          `}</style>

          {/* Header strip */}
          <div
            style={{
              padding:    '10px 14px 8px',
              borderBottom: `1px solid ${style.fill}18`,
              background:  `linear-gradient(135deg, ${style.fill}0a 0%, transparent 60%)`,
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold tracking-wide" style={{ color: 'rgb(230,235,245)' }}>
                {staticContent.label}
              </span>
              {/* Status pill */}
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest"
                style={{
                  background: style.badge,
                  color:      style.fill,
                  border:     `1px solid ${style.fill}30`,
                }}
              >
                <span
                  style={{
                    display:     'inline-block',
                    width:        5,
                    height:       5,
                    borderRadius: '50%',
                    background:   style.fill,
                    boxShadow:    style.glow,
                    animation:    level === 'green' ? 'none' : 'frisPulse 1.8s ease-in-out infinite',
                  }}
                />
                <style>{`@keyframes frisPulse { 0%,100%{opacity:1}50%{opacity:0.45} }`}</style>
                {style.label}
              </span>
            </div>
            <p className="text-[10px] leading-relaxed" style={{ color: 'rgba(180,190,210,0.65)' }}>
              {summary}
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: '10px 14px 12px' }}>

            {/* What's Working */}
            {staticContent.working.length > 0 && (
              <div className="mb-3">
                <div
                  className="text-[9px] uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5"
                  style={{ color: '#34D39988' }}
                >
                  <CheckCircle2 className="h-2.5 w-2.5" style={{ color: '#34D399' }} />
                  What&apos;s Working
                </div>
                <ul className="space-y-1">
                  {staticContent.working.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span style={{ color: '#34D399', marginTop: 2, flexShrink: 0, fontSize: 9 }}>✓</span>
                      <span className="text-[10px] leading-relaxed" style={{ color: 'rgba(200,210,230,0.75)' }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* What's Coming */}
            {staticContent.coming.length > 0 && (
              <div className={staticContent.notes ? 'mb-3' : ''}>
                <div
                  className="text-[9px] uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5"
                  style={{ color: '#FBBF2488' }}
                >
                  <ArrowRight className="h-2.5 w-2.5" style={{ color: '#FBBF24' }} />
                  What&apos;s Coming Next
                </div>
                <ul className="space-y-1">
                  {staticContent.coming.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span style={{ color: '#FBBF24', marginTop: 2, flexShrink: 0, fontSize: 8 }}>→</span>
                      <span className="text-[10px] leading-relaxed" style={{ color: 'rgba(200,210,230,0.55)' }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Notes */}
            {staticContent.notes && (
              <div
                className="flex items-start gap-2 mt-2 pt-2"
                style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
              >
                <Info className="h-3 w-3 mt-0.5 shrink-0" style={{ color: 'rgba(200,210,230,0.35)' }} />
                <p className="text-[9px] leading-relaxed" style={{ color: 'rgba(200,210,230,0.4)' }}>
                  {staticContent.notes}
                </p>
              </div>
            )}

            {/* Live deps from FRIS probe */}
            {liveProbe?.dependencies && liveProbe.dependencies.length > 0 && (
              <div
                className="mt-2 pt-2"
                style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
              >
                <div className="text-[9px] uppercase tracking-[0.18em] mb-2" style={{ color: 'rgba(180,190,210,0.4)' }}>
                  Live Dependencies
                </div>
                <div className="space-y-1">
                  {liveProbe.dependencies.map((dep) => (
                    <div key={dep.id} className="flex items-center gap-2">
                      <span
                        style={{
                          display:     'inline-block',
                          width:        5,
                          height:       5,
                          borderRadius: '50%',
                          background:   dep.ok ? '#34D399' : '#F87171',
                          flexShrink:   0,
                        }}
                      />
                      <span className="text-[9px]" style={{ color: 'rgba(200,210,230,0.55)' }}>
                        {dep.label}
                        {dep.reason && <span style={{ color: 'rgba(200,210,230,0.35)' }}> — {dep.reason}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding:    '6px 14px',
              borderTop:  '1px solid rgba(255,255,255,0.04)',
              display:    'flex',
              alignItems: 'center',
              gap:        6,
            }}
          >
            <AlertCircle className="h-2.5 w-2.5" style={{ color: 'rgba(180,190,210,0.3)' }} />
            <span style={{ fontSize: 9, color: 'rgba(180,190,210,0.3)', letterSpacing: '0.08em' }}>
              RCD² Engine · FRIS v1 · {liveProbe ? `checked ${Math.round((Date.now() - liveProbe.lastChecked) / 1000)}s ago` : 'static readiness data'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
